import { fetchSenateAPI, fetchSenateAPIWithLocalStorage } from "@/apis/core";
import { fetcher } from "@/apis/fetcher";
import { localStorageKey } from "@/configs/enum";
import { getToken } from "@/utils/auth";
import { setWithExpiry } from "@/utils/helper";
import _ from "lodash";
import { otherPath, registerPath } from "../path";
import type {
  PostActionViewFileHistoryRequest,
  PostActionViewFileHistoryResponse,
  PostAddressRequest,
  PostAddressResponse,
  PostAffiliationRequest,
  PostAffiliationResponse,
  PostBusinessRequest,
  PostBusinessResponse,
  PostCancelRequest,
  PostCancelResponse,
  PostCandidateRequest,
  PostCandidateResponse,
  PostConceptRequest,
  PostConceptResponse,
  PostConsentRequest,
  PostConsentResponse,
  PostDeleteFileRequest,
  PostDeleteFileResponse,
  PostDisciplineRequest,
  PostDisciplineResponse,
  PostEducationRequest,
  PostEducationResponse,
  PostExperienceRequest,
  PostExperienceResponse,
  PostExpertiseOrExperienceRequest,
  PostExpertiseOrExperienceResponse,
  PostForgotPasswordRequest,
  PostForgotPasswordResponse,
  PostIncompatibilityRequest,
  PostIncompatibilityResponse,
  PostKnowledgeAndAbilityRequest,
  PostKnowledgeAndAbilityResponse,
  PostKnowledgeRequest,
  PostKnowledgeResponse,
  PostLogInSSORequest,
  PostLogInSSOResponse,
  PostOccupationRequest,
  PostOccupationResponse,
  PostPersonalRecordRequest,
  PostPersonalRecordResponse,
  PostPoliticalRequest,
  PostPoliticalResponse,
  PostPropertyRequest,
  PostPropertyResponse,
  PostReportApplicateRequest,
  PostReportApplicateResponse,
  PostReportCVRequest,
  PostReportCVResponse,
  PostResetPasswordRequest,
  PostResetPasswordResponse,
  PostSignInRequest,
  PostSignInResponse,
  PostSubscriptionRequest,
  PostSubscriptionResponse,
  PostUploadFile2Request,
  PostUploadFile2Response,
  PostUploadFileRequest,
  PostUploadFileResponse,
  PostUserProfileRequest,
  PostUserProfileResponse,
  PostVerifyAccountRequest,
  PostVerifyAccountResponse,
} from "./type";

const method = "post";
const VITE_SENATE_URL = import.meta.env.VITE_SENATE_URL;

async function postConsent(
  request: PostConsentRequest,
): Promise<PostConsentResponse> {
  const pathname = registerPath.consent;
  const response = await fetchSenateAPI({
    method,
    pathname,
    request,
    debug: "postPersonalRecord",
  });
  return response;
}

async function postPersonalRecord(
  request: PostPersonalRecordRequest,
): Promise<PostPersonalRecordResponse> {
  const pathname = registerPath.personalRecord;
  const response = await fetchSenateAPI({
    method,
    pathname,
    request,
    debug: "postPersonalRecord",
  });
  return response;
}

async function postAddress(
  request: PostAddressRequest,
): Promise<PostAddressResponse> {
  const pathname = registerPath.address;
  const response = await fetchSenateAPI({
    method,
    pathname,
    request,
    debug: "postAddress",
  });
  return response;
}

async function postEducation(
  request: PostEducationRequest,
): Promise<PostEducationResponse> {
  const pathname = registerPath.education;
  const response = await fetchSenateAPI({
    method,
    pathname,
    request,
    debug: "postEducation",
  });
  return response;
}

async function postExperience(
  request: PostExperienceRequest,
): Promise<PostExperienceResponse> {
  const pathname = registerPath.experience;
  const response = await fetchSenateAPI({
    method,
    pathname,
    request,
    debug: "postExperience",
  });
  return response;
}

async function postProperty(
  request: PostPropertyRequest,
): Promise<PostPropertyResponse> {
  const pathname = registerPath.property;
  const response = await fetchSenateAPI({
    method,
    pathname,
    request,
    debug: "postProperty",
  });
  return response;
}

async function postOccupation(
  request: PostOccupationRequest,
): Promise<PostOccupationResponse> {
  const pathname = registerPath.occupation;
  const response = await fetchSenateAPI({
    method,
    pathname,
    request,
    debug: "postOccupation",
  });
  return response;
}

async function postBusiness(
  request: PostBusinessRequest,
): Promise<PostBusinessResponse> {
  const pathname = registerPath.business;
  const response = await fetchSenateAPI({
    method,
    pathname,
    request,
    debug: "postBusiness",
  });
  return response;
}

async function postIncompatibility(
  request: PostIncompatibilityRequest,
): Promise<PostIncompatibilityResponse> {
  const pathname = registerPath.incompatibility;
  const response = await fetchSenateAPI({
    method,
    pathname,
    request,
    debug: "postIncompatibility",
  });
  return response;
}

async function postPolitical(
  request: PostPoliticalRequest,
): Promise<PostPoliticalResponse> {
  const pathname = registerPath.political;
  const response = await fetchSenateAPI({
    method,
    pathname,
    request,
    debug: "postPolitical",
  });
  return response;
}

async function postDiscipline(
  request: PostDisciplineRequest,
): Promise<PostDisciplineResponse> {
  const pathname = registerPath.discipline;
  const response = await fetchSenateAPI({
    method,
    pathname,
    request,
    debug: "postDiscipline",
  });
  return response;
}

async function postKnowledge(
  request: PostKnowledgeRequest,
): Promise<PostKnowledgeResponse> {
  const pathname = registerPath.knowledge;
  const response = await fetchSenateAPI({
    method,
    pathname,
    request,
    debug: "postKnowledge",
  });
  return response;
}

async function postConcept(
  request: PostConceptRequest,
): Promise<PostConceptResponse> {
  const pathname = registerPath.concept;
  const response = await fetchSenateAPI({
    method,
    pathname,
    request,
    debug: "postConcept",
  });
  return response;
}

async function postAffiliation(
  request: PostAffiliationRequest,
): Promise<PostAffiliationResponse> {
  const pathname = registerPath.affiliation;
  const response = await fetchSenateAPI({
    method,
    pathname,
    request,
    debug: "postAffiliation",
  });
  return response;
}

async function postUploadFile(
  request: PostUploadFileRequest,
): Promise<PostUploadFileResponse> {
  const pathname = registerPath.uploadFile;
  const response = await fetchSenateAPI({
    method,
    pathname,
    request,
    debug: "postUploadFile",
  });
  return response;
}

async function postUploadFile2(
  request: PostUploadFile2Request,
): Promise<PostUploadFile2Response> {
  const pathname = registerPath.uploadFile2;
  const response = await fetchSenateAPI({
    method,
    pathname,
    request,
    debug: "postUploadFile2",
    contentType: "multipart/form-data",
  });
  return response;
}

async function postDeleteFile(
  request: PostDeleteFileRequest,
): Promise<PostDeleteFileResponse> {
  const pathname = registerPath.deleteFile;
  const response = await fetchSenateAPI({
    method,
    pathname,
    request,
    debug: "postDeleteFile",
  });
  return response;
}

async function postKnowledgeAndAbility(
  request: PostKnowledgeAndAbilityRequest,
): Promise<PostKnowledgeAndAbilityResponse> {
  const pathname = registerPath.knowledgeAndAbility;
  const response = await fetchSenateAPI({
    method,
    pathname,
    request,
    debug: "postKnowledgeAndAbility",
  });
  return response;
}

async function postExpertiseOrExperience(
  request: PostExpertiseOrExperienceRequest,
): Promise<PostExpertiseOrExperienceResponse> {
  const pathname = registerPath.expertiseOrExperience;
  const response = await fetchSenateAPI({
    method,
    pathname,
    request,
    debug: "postExpertiseOrExperience",
  });
  return response;
}

async function postCandidate(
  request: PostCandidateRequest,
): Promise<PostCandidateResponse> {
  const pathname = registerPath.candidate;
  const response = await fetchSenateAPI({
    method,
    pathname,
    request,
    debug: "postCandidate",
  });
  return response;
}

async function postSubscription(
  request: PostSubscriptionRequest,
): Promise<PostSubscriptionResponse> {
  const pathname = registerPath.subscription;
  const response = await fetchSenateAPI({
    method,
    pathname,
    request,
    debug: "postSubscription",
    auth: false,
  });
  return response;
}

async function postSignIn(
  request: PostSignInRequest,
): Promise<PostSignInResponse> {
  const pathname = registerPath.signIn;
  const response = await fetchSenateAPIWithLocalStorage({
    method,
    pathname,
    request,
    debug: "postSignIn",
    key: localStorageKey.REGISTER_USER_INFORMATION,
    auth: false,
  });
  return response;
}

async function postVerifyAccount(
  request: PostVerifyAccountRequest,
): Promise<PostVerifyAccountResponse> {
  const pathname = registerPath.verifyAccount;
  const response = await fetchSenateAPI({
    method,
    pathname,
    request,
    debug: "postVerifyAccount",
    auth: false,
  });
  return response;
}

async function postForgotPassword(
  request: PostForgotPasswordRequest,
): Promise<PostForgotPasswordResponse> {
  const pathname = registerPath.forgotPassword;
  const response = await fetchSenateAPI({
    method,
    pathname,
    request,
    debug: "postForgotPassword",
    auth: false,
  });
  return response;
}

async function postResetPassword(
  request: PostResetPasswordRequest,
): Promise<PostResetPasswordResponse> {
  const pathname = registerPath.resetPassword;
  const response = await fetchSenateAPI({
    method,
    pathname,
    request,
    debug: "postResetPassword",
    auth: false,
  });
  return response;
}

async function postUserProfile(
  request: PostUserProfileRequest,
): Promise<PostUserProfileResponse> {
  const pathname = registerPath.userProfile;
  const response = await fetchSenateAPI({
    method,
    pathname,
    request,
    debug: "postUserProfile",
    auth: true,
  });
  if (response.status === 201 && !_.isEmpty(response.data)) {
    setWithExpiry(
      localStorageKey.REGISTER_USER_INFORMATION,
      response,
      120 * 60000,
    );
  }
  return { ...response, data: null };
}

async function postSignInSSO(
  request: PostLogInSSORequest,
): Promise<PostLogInSSOResponse> {
  const pathname = otherPath.sso;
  const response = await fetchSenateAPIWithLocalStorage({
    method,
    pathname,
    request,
    debug: "postSignInSSO",
    key: localStorageKey.REGISTER_USER_INFORMATION,
    auth: false,
  });
  return response;
}

async function postReportCV(
  request: PostReportCVRequest,
): Promise<PostReportCVResponse> {
  const token = getToken();
  const response = await fetcher({
    method,
    url: `${VITE_SENATE_URL}/api/${otherPath.reportCV}`,
    responseType: "arraybuffer",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json, text/plain, */*",
      Authorization: `Bearer ${token}`,
    },
    maxBodyLength: Infinity,
    data: { ...request, site: "recruit-online" },
  });
  return response;
}

async function postReportApplicate(
  request: PostReportApplicateRequest,
): Promise<PostReportApplicateResponse> {
  const token = getToken();
  const response = await fetcher({
    method,
    url: `${VITE_SENATE_URL}/api/${otherPath.reportApplicate}`,
    responseType: "arraybuffer",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json, text/plain, */*",
      Authorization: `Bearer ${token}`,
    },
    maxBodyLength: Infinity,
    data: { ...request, site: "recruit-online" },
  });
  return response;
}

async function postCancel({
  senateRecruitDetailId,
  candidateInfoId,
}: PostCancelRequest): Promise<PostCancelResponse> {
  const token = getToken();
  const response = await fetcher({
    method,
    url: `${VITE_SENATE_URL}/api/${registerPath.cancel}`,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: {
      senateRecruitDetailId,
      candidateInfoId,
    },
  });
  return response;
}

async function postActionViewFileHistory(
  request: PostActionViewFileHistoryRequest,
): Promise<PostActionViewFileHistoryResponse> {
  const token = getToken();
  const response = await fetcher({
    method,
    url: `${VITE_SENATE_URL}/api/${registerPath.actionViewFileHistory}`,
    responseType: "arraybuffer",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json, text/plain, */*",
      Authorization: `Bearer ${token}`,
    },
    maxBodyLength: Infinity,
    data: { ...request },
  });
  return response;
}

export {
  postActionViewFileHistory,
  postAddress,
  postAffiliation,
  postBusiness,
  postCancel,
  postCandidate,
  postConcept,
  postConsent,
  postDeleteFile,
  postDiscipline,
  postEducation,
  postExperience,
  postExpertiseOrExperience,
  postForgotPassword,
  postIncompatibility,
  postKnowledge,
  postKnowledgeAndAbility,
  postOccupation,
  postPersonalRecord,
  postPolitical,
  postProperty,
  postReportApplicate,
  postReportCV,
  postResetPassword,
  postSignIn,
  postSignInSSO,
  postSubscription,
  postUploadFile,
  postUploadFile2,
  postUserProfile,
  postVerifyAccount,
};
